"use client";

import dynamic from "next/dynamic";
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { cn } from "@workspace/ui";
import { Icons } from "@workspace/ui/components/icons";
import { Slider } from "@workspace/ui/components/slider";
import { Spinner } from "@workspace/ui/components/spinner";

import {
  MAX_PLAYED,
  useSmoothPlayedProgress,
  useVideoControlsVisibility,
  useVideoFullscreen,
  useVideoLoadingState,
  useVisibilityAutoPause,
} from "./video.hooks";

import type { ReactNode, RefObject } from "react";

const ReactPlayer = dynamic(() => import("react-player"), { ssr: false });

interface VideoProps {
  readonly children: ReactNode;
  readonly className?: string;
}

interface VideoSourceProps {
  readonly src: string;
}

interface VideoContextValue {
  readonly src: string;
  readonly registerSource: (src: string) => void;
  readonly containerRef: RefObject<HTMLDivElement | null>;
  readonly playerRef: RefObject<HTMLVideoElement | null>;
  readonly controlsShown: boolean;
  readonly fullscreen: boolean;
  readonly isLoading: boolean;
  readonly playing: boolean;
  readonly enterFullscreen: () => void;
  readonly exitFullscreen: () => void;
  readonly handlePlayerPlaying: () => void;
  readonly handlePlayerReady: () => void;
  readonly handlePlayerError: () => void;
  readonly handlePlayerSeeked: () => void;
  readonly handleTimeUpdate: () => void;
  readonly hideControls: () => void;
  readonly showControls: () => void;
  readonly startLoading: () => void;
  readonly stopLoading: () => void;
  readonly togglePlaying: () => void;
}

interface VideoProgressContextValue {
  readonly played: number;
  readonly handleSeekChange: (value: number | readonly number[]) => void;
}

const VideoContext = createContext<VideoContextValue | null>(null);
const VideoProgressContext = createContext<VideoProgressContextValue | null>(
  null,
);

const patchedPlayElements = new WeakSet<HTMLMediaElement>();
const autoplayBlockedCallbacks = new WeakMap<HTMLMediaElement, () => void>();

const patchMediaElementPlay = (
  element: HTMLMediaElement,
  onAutoplayBlocked: () => void,
) => {
  autoplayBlockedCallbacks.set(element, onAutoplayBlocked);

  if (patchedPlayElements.has(element)) {
    return;
  }

  patchedPlayElements.add(element);

  const originalPlay = element.play.bind(element);

  element.play = () => {
    const result = originalPlay();

    return result.catch((error: unknown) => {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }

      autoplayBlockedCallbacks.get(element)?.();
    });
  };
};

const Fallback = () => (
  <div className="flex h-full min-h-64 w-full items-center justify-center">
    <Spinner className="size-10" />
    <span className="sr-only">Loading...</span>
  </div>
);

const useVideoContext = () => {
  const context = useContext(VideoContext);

  if (!context) {
    throw new Error("Video compound components must be used within <Video />.");
  }

  return context;
};

const useVideoProgressContext = () => {
  const context = useContext(VideoProgressContext);

  if (!context) {
    throw new Error("Video progress components must be used within <Video />.");
  }

  return context;
};

export const Video = memo<VideoProps>(({ children, className }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [wantsToPlay, setWantsToPlay] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<HTMLVideoElement | null>(null);
  const { controlsShown, showControls, hideControls } =
    useVideoControlsVisibility();
  const { fullscreen, enterFullscreen, exitFullscreen } =
    useVideoFullscreen(containerRef);
  const videoSrc = src ?? "";
  const { isLoading, startLoading, stopLoading } =
    useVideoLoadingState(videoSrc);
  const { isAutoPaused } = useVisibilityAutoPause({
    containerRef,
    fullscreen,
    src: videoSrc,
  });
  const playing = Boolean(videoSrc) && wantsToPlay && !isAutoPaused;
  const { played, handleSeekChange, handleSeeked, handleTimeUpdate } =
    useSmoothPlayedProgress({
      playerRef,
      src: videoSrc,
      playing,
      controlsShown,
      fullscreen,
    });

  const registerSource = useCallback((nextSrc: string) => {
    setSrc((currentSrc) => (currentSrc === nextSrc ? currentSrc : nextSrc));
  }, []);

  useEffect(() => {
    if (!videoSrc) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      setWantsToPlay(true);
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [videoSrc]);

  const togglePlaying = useCallback(() => {
    setWantsToPlay((currentPlaying) => !currentPlaying);
  }, []);

  const handlePlayerReady = useCallback(() => {
    stopLoading();
  }, [stopLoading]);

  const handlePlayerError = useCallback(() => {
    setWantsToPlay(false);
    stopLoading();
  }, [stopLoading]);

  const handlePlayerPlaying = useCallback(() => {
    stopLoading();
  }, [stopLoading]);

  const handlePlayerSeeked = useCallback(() => {
    handleSeeked();
    stopLoading();
  }, [handleSeeked, stopLoading]);

  const contextValue = useMemo<VideoContextValue>(
    () => ({
      src: videoSrc,
      registerSource,
      containerRef,
      playerRef,
      controlsShown,
      fullscreen,
      isLoading,
      playing,
      enterFullscreen,
      exitFullscreen,
      handlePlayerPlaying,
      handlePlayerReady,
      handlePlayerError,
      handlePlayerSeeked,
      handleTimeUpdate,
      hideControls,
      showControls,
      startLoading,
      stopLoading,
      togglePlaying,
    }),
    [
      controlsShown,
      enterFullscreen,
      exitFullscreen,
      fullscreen,
      handlePlayerError,
      handlePlayerPlaying,
      handlePlayerReady,
      handlePlayerSeeked,
      handleTimeUpdate,
      hideControls,
      isLoading,
      playing,
      registerSource,
      showControls,
      startLoading,
      stopLoading,
      togglePlaying,
      videoSrc,
    ],
  );

  const progressContextValue = useMemo<VideoProgressContextValue>(
    () => ({
      played,
      handleSeekChange,
    }),
    [handleSeekChange, played],
  );

  return (
    <div
      className={cn(
        "prose-video:my-0 @container/video relative overflow-hidden rounded-xl",
        className,
      )}
      ref={containerRef}
      onMouseEnter={showControls}
      onMouseMove={showControls}
      onMouseLeave={hideControls}
    >
      <VideoContext.Provider value={contextValue}>
        <VideoProgressContext.Provider value={progressContextValue}>
          {children}
          {src ? null : <Fallback />}
        </VideoProgressContext.Provider>
      </VideoContext.Provider>
    </div>
  );
});

export const VideoSource = memo<VideoSourceProps>(({ src }) => {
  const {
    registerSource,
    playerRef,
    playing,
    startLoading,
    stopLoading,
    handlePlayerReady,
    handlePlayerError,
    handlePlayerPlaying,
    handlePlayerSeeked,
    handleTimeUpdate,
    isLoading,
  } = useVideoContext();

  useEffect(() => {
    registerSource(src);
  }, [registerSource, src]);

  // Pause before the <video> is torn down (unmount or src swap) so pending
  // play() promises are not rejected with AbortError when the node is removed.
  useLayoutEffect(() => {
    return () => {
      // Read ref in cleanup: the media node may attach after the effect body runs.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      playerRef.current?.pause();
    };
  }, [playerRef, src]);

  const setPlayerRef = useCallback(
    (node: HTMLVideoElement | null) => {
      playerRef.current = node;

      if (node) {
        patchMediaElementPlay(node, handlePlayerError);
      }
    },
    [handlePlayerError, playerRef],
  );

  return (
    <>
      <ReactPlayer
        src={src}
        ref={setPlayerRef}
        width="100%"
        height="100%"
        muted
        playing={playing}
        loop
        playsInline
        onLoadStart={startLoading}
        onReady={handlePlayerReady}
        onError={handlePlayerError}
        onStart={stopLoading}
        onPlaying={handlePlayerPlaying}
        onWaiting={startLoading}
        onPause={stopLoading}
        onSeeked={handlePlayerSeeked}
        onTimeUpdate={handleTimeUpdate}
        fallback={<Fallback />}
      />
      {isLoading && playing ? (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-black/10">
          <Fallback />
        </div>
      ) : null}
    </>
  );
});

export const VideoControls = memo(() => {
  const context = useVideoContext();

  return (
    <div
      className={cn(
        "opacity-0 transition-opacity duration-300",
        context.controlsShown ? "opacity-100" : "opacity-0",
      )}
    >
      <button
        type="button"
        className={cn(
          "absolute inset-0 z-10 flex items-center justify-center",
          {
            "cursor-default": !context.controlsShown,
          },
        )}
        onClick={context.togglePlaying}
      >
        <div
          className={cn(
            "relative flex size-10 items-center justify-center rounded-full bg-black/50 p-2.5 text-white transition-colors hover:bg-black/60",
            "@2xs/video:size-14 @2xs/video:p-3.5",
            "@md/video:size-16 @md/video:p-4.5",
            "@lg/video:size-18 @lg/video:p-5",
            "@xl/video:size-20 @xl/video:p-5.5",
          )}
        >
          {context.playing ? (
            <Icons.PauseIcon className="size-full" />
          ) : (
            <Icons.PlayIcon className="size-full" />
          )}
          <span className="sr-only">{context.playing ? "Pause" : "Play"}</span>
        </div>
      </button>

      <div className="absolute inset-x-0 bottom-0 z-20">
        <div className="relative">
          <VideoProgressSlider controlsShown={context.controlsShown} />

          <button
            type="button"
            className={cn(
              "absolute -top-1.5 right-1.5 flex size-4 -translate-y-full items-center justify-center rounded-sm bg-black/50 p-0.5 text-white transition-colors hover:bg-black/60",
              "@2xs/video:size-6.5 @2xs/video:p-1",
              "@md/video:-top-2 @md/video:right-2 @md/video:size-7 @md/video:p-1",
              "@lg/video:-top-2.5 @lg/video:right-2.5 @lg/video:size-8 @lg/video:rounded-lg @lg/video:p-1.5",
              "@xl/video:-top-3 @xl/video:right-3 @xl/video:size-9 @xl/video:rounded-lg",
            )}
            onClick={
              context.fullscreen
                ? context.exitFullscreen
                : context.enterFullscreen
            }
          >
            {context.fullscreen ? (
              <Icons.Minimize className="size-full" />
            ) : (
              <Icons.FullScreen className="size-full" />
            )}
            <span className="sr-only">
              {context.fullscreen ? "Minimize" : "Maximize"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
});

interface VideoProgressSliderProps {
  readonly controlsShown: boolean;
}

const VideoProgressSlider = memo<VideoProgressSliderProps>(
  ({ controlsShown }) => {
    const { played, handleSeekChange } = useVideoProgressContext();

    return (
      <Slider
        value={played}
        onValueChange={handleSeekChange}
        min={0}
        max={MAX_PLAYED}
        step={0.01}
        thumb={false}
        className={cn({
          "cursor-pointer": controlsShown,
        })}
        indicatorClassName="transition-[width] ease-linear"
      />
    );
  },
);
